<script setup lang="ts">
import FinestCard from '@/components/FinestCard.vue'
import CreateUserDialog from '@/pages/banks/CreateUserDialog.vue'
import EditUserDialog from '@/pages/banks/EditUserDialog.vue'
import { Paginated } from '@/types'
import BankerAccountController from '@controllers/Banks/BankerAccountController'
import { router } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BankerAccountData = App.Data.Account.BankerAccountData

const { t } = useI18n()
const confirm = useConfirm()

const props = defineProps<{
  users: Paginated<BankerAccountData>
  pendingEmailChanges: Record<string, { new_email: string; expires_at?: string | null }>
  filters: {
    search?: string
    per_page: number
  }
}>()

const searchText = ref(props.filters.search ?? '')
const editingUser = ref<BankerAccountData | null>(null)
const isEditDialogVisible = ref(false)
let timeout: ReturnType<typeof setTimeout> | null = null

function openEditDialog(user: BankerAccountData) {
  editingUser.value = user
  isEditDialogVisible.value = true
}

function onPage(event: { page: number; rows: number }) {
  getPaginatedUsers({
    page: event.page + 1,
    per_page: event.rows,
  })
}

watch(searchText, (value) => {
  if (timeout) {
    clearTimeout(timeout)
  }

  timeout = setTimeout(() => {
    getPaginatedUsers({
      search: value,
      page: 1,
    })
  }, 300)
})

function getPaginatedUsers(params: { page?: number; per_page?: number; search?: string }) {
  router.get(
    BankerAccountController.index().url,
    {
      page: params.page ?? props.users.current_page,
      per_page: params.per_page ?? props.filters.per_page,
      search: params.search ?? searchText.value,
    },
    {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    },
  )
}

function pendingEmailChange(user: BankerAccountData) {
  if (!user.id) {
    return null
  }

  return props.pendingEmailChanges[user.id] ?? null
}

function deleteUser(user: BankerAccountData) {
  confirm.require({
    header: t('confirm.header'),
    message: t('confirm.delete', {
      name: user.first_name + ' ' + user.last_name,
    }),
    rejectProps: {
      label: t('confirm.reject'),
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: t('confirm.accept'),
      severity: 'danger',
    },
    accept: () => {
      if (!user.id) return

      router.delete(BankerAccountController.destroy({ bankerAccount: user.id }).url, {
        preserveScroll: true,
      })
    },
  })
}
</script>

<template>
  <FinestCard :title="t('title')" icon="pi pi-users">
    <template #actions>
      <CreateUserDialog />
    </template>

    <DataTable
      :value="users.data"
      data-key="id"
      :rows="10"
      striped-rows
      responsive-layout="scroll"
      class="w-full"
      :pt="{
        header: { class: '!pl-0' },
      }">
      <template #header>
        <div class="flex justify-start">
          <IconField class="w-full sm:w-64">
            <InputIcon>
              <i class="pi pi-search" />
            </InputIcon>
            <InputText v-model="searchText" :placeholder="t('search')" fluid />
          </IconField>
        </div>
      </template>

      <template #empty>{{ t('no_users_found') }}</template>

      <Column field="first_name" :header="t('columns.first_name')" style="width: 20%" />
      <Column field="last_name" :header="t('columns.last_name')" style="width: 20%" />
      <Column field="email" :header="t('columns.email')" style="width: 30%">
        <template #body="{ data }: { data: BankerAccountData }">
          <div class="flex flex-col">
            <span>{{ data.email }}</span>
            <small v-if="pendingEmailChange(data)" class="text-surface-500">
              {{ t('pending_email_change', { email: pendingEmailChange(data)?.new_email }) }}
            </small>
          </div>
        </template>
      </Column>
      <Column :header="t('columns.role')" style="width: 10%">
        <template #body="{ data }: { data: BankerAccountData }">
          <Tag
            :value="data.is_admin ? t('roles.admin') : t('roles.banker')"
            :severity="data.is_admin ? 'info' : 'secondary'" />
        </template>
      </Column>

      <Column
        field="is_email_verified"
        style="width: 5%"
        header-style="text-align:center"
        body-style="text-align:center"
        :pt="{ headerContent: { class: 'justify-center' } }">
        <template #header>
          <span class="inline-flex items-center gap-1">
            {{ t('columns.verified') }}
            <i
              v-tooltip.top="t('tooltips.verified')"
              class="pi pi-info-circle text-surface-400 cursor-help text-sm!"
              aria-hidden="true" />
          </span>
        </template>
        <template #body="{ data }: { data: BankerAccountData }">
          <i v-if="data.is_email_verified" class="pi pi-check text-success-800" />
          <i v-else class="pi pi-minus text-surface-400" />
        </template>
      </Column>

      <Column
        field="is_active"
        style="width: 5%"
        header-style="text-align:center"
        body-style="text-align:center"
        :pt="{ headerContent: { class: 'justify-center' } }">
        <template #header>
          <span class="inline-flex items-center gap-1">
            {{ t('columns.active') }}
            <i
              v-tooltip.top="t('tooltips.active')"
              class="pi pi-info-circle text-surface-400 cursor-help text-sm!"
              aria-hidden="true" />
          </span>
        </template>
        <template #body="{ data }: { data: BankerAccountData }">
          <i v-if="data.is_active" class="pi pi-check text-success-800" />
          <i v-else class="pi pi-ban text-danger" />
        </template>
      </Column>

      <Column
        :header="t('columns.actions')"
        style="width: 7rem; min-width: 7rem"
        body-style="text-align:center"
        :pt="{ headerContent: { class: 'justify-center' } }">
        <template #body="{ data }: { data: BankerAccountData }">
          <div class="flex justify-center gap-1">
            <Button
              v-tooltip.top="t('actions.edit')"
              icon="pi pi-pencil"
              size="small"
              severity="secondary"
              text
              :aria-label="t('actions.edit')"
              @click="openEditDialog(data)" />
            <Button
              v-tooltip.top="t('actions.delete')"
              icon="pi pi-trash"
              size="small"
              severity="secondary"
              text
              :aria-label="t('actions.delete')"
              @click="deleteUser(data)" />
          </div>
        </template>
      </Column>
    </DataTable>
  </FinestCard>

  <EditUserDialog
    v-if="editingUser"
    v-model:visible="isEditDialogVisible"
    :user="editingUser"
    :pending-email-change="pendingEmailChange(editingUser)" />

  <div class="mt-4 flex justify-center">
    <Paginator
      :rows="users.per_page"
      :total-records="users.total"
      :first="(users.current_page - 1) * users.per_page"
      @page="onPage">
      <template #container="{ page, pageCount }">
        <div class="flex items-center gap-4">
          <Button
            icon="pi pi-chevron-left"
            text
            :disabled="page === 0"
            @click="onPage({ page: page - 1, rows: users.per_page })" />

          <span class="text-sm">Page {{ page + 1 }} / {{ pageCount }}</span>

          <Button
            icon="pi pi-chevron-right"
            text
            :disabled="pageCount ? page === pageCount - 1 : true"
            @click="onPage({ page: page + 1, rows: users.per_page })" />
        </div>
      </template>
    </Paginator>
  </div>
</template>

<i18n>
{
  "en": {
    "confirm": {
      "header": "Delete User",
      "delete": "Are you sure you want to permanently delete {name}?",
      "reject": "Cancel",
      "accept": "Delete"
    },
    "title": "User Management",
    "search": "Search",
    "no_users_found": "No users found.",
    "pending_email_change": "Pending confirmation for {email}",
    "columns": {
      "first_name": "First Name",
      "last_name": "Last Name",
      "email": "Email",
      "role": "Role",
      "verified": "Verified",
      "active": "Active",
      "actions": "Actions",
      "password": "Password"
    },
    "roles": {
      "admin": "Admin",
      "banker": "Banker"
    },
    "tooltips": {
      "verified": "Email address has been confirmed",
      "active": "User can sign in"
    },
    "actions": {
      "edit": "Edit",
      "delete": "Delete"
    }
  },
  "de": {
    "confirm": {
      "header": "Benutzer löschen",
      "delete": "Möchtest du {name} wirklich unwiderruflich löschen?",
      "reject": "Abbrechen",
      "accept": "Löschen"
    },
    "title": "Benutzerverwaltung",
    "search": "Suche",
    "no_users_found": "Keine Nutzer gefunden.",
    "pending_email_change": "Bestätigung für {email} ausstehend",
    "columns": {
      "first_name": "Vorname",
      "last_name": "Nachname",
      "email": "E-Mail",
      "role": "Rolle",
      "verified": "Verifiziert",
      "active": "Aktiv",
      "actions": "Aktionen",
      "password": "Passwort"
    },
    "roles": {
      "admin": "Admin",
      "banker": "Banker"
    },
    "tooltips": {
      "verified": "E-Mail-Adresse wurde bestätigt",
      "active": "Nutzer kann sich anmelden"
    },
    "actions": {
      "edit": "Bearbeiten",
      "delete": "Löschen"
    }
  }
}
</i18n>
